import { SortRecipeCalculationsBy } from '@/pages/authorized/recipe-page/common/recipe-header/sort-recipe-calculations-selector/consts';
import { sortRecipeCalculations } from '@/pages/authorized/recipe-page/common/recipe-header/sort-recipe-calculations-selector/utils';
import { RecipeCalculationDto, RecipeCalculationQuery } from '@/services/api/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import constate from 'constate';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import {
  addNewRecipeCalculation,
  reorderRecipeCalculations,
} from '../../helpers/queries/query-helpers';

import {
  CalculationDraft,
  CalculationDraftMode,
  CalculationDraftTemplate,
  isCalculationDraftId,
} from './calculationDrafts';
import {
  createRecipeCalculationsStore,
  RecipeCalculationsState,
  SelectedCalculatorState,
} from './recipeCalculationsStore';
import { useRecipeParam } from './useRecipeParam';

export type { SelectedCalculatorState } from './recipeCalculationsStore';

// Server-defined display order. `?? 0` defends against calculations hydrated from the
// persisted query cache before the sortOrder field existed; ties break by id (creation order).
const byServerOrder = (a: RecipeCalculationDto, b: RecipeCalculationDto) =>
  (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id;

const useRecipeCalculationsValue = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { recipeId } = useRecipeParam();

  // Transient state (stull points, dirty flags, selection) lives in an external store so that a
  // write by one card does not re-render every other card. See recipeCalculationsStore.ts.
  const [store] = useState(createRecipeCalculationsStore);

  // Client-side-only display order for readonly recipes (viewers can re-sort but not persist).
  const [ephemeralOrder, setEphemeralOrder] = useState<number[] | null>(null);

  // The unpersisted "new calculation" cards currently on the board. See calculationDrafts.ts.
  const [drafts, setDrafts] = useState<CalculationDraft[]>([]);
  // Ids run downwards from -1 so every draft of this recipe gets its own, never reused.
  const nextDraftIdRef = useRef(-1);

  const calculationsQuery = RecipeCalculationQuery.useGetAllQuery(recipeId);

  const calculations = useMemo(() => {
    const list =
      calculationsQuery.data
        // Filtering deleted calculation on frontend part
        // to be able to show removed calculators in investigation editor
        ?.filter((x) => !x.isDeleted)
        .sort(byServerOrder) ?? [];

    if (!ephemeralOrder) {
      return list;
    }

    // Readonly recipes overlay a client-side order; unknown ids fall to the end in server order.
    const positionById = new Map(ephemeralOrder.map((id, index) => [id, index]));
    return [...list].sort(
      (a, b) =>
        (positionById.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
        (positionById.get(b.id) ?? Number.MAX_SAFE_INTEGER),
    );
  }, [calculationsQuery.data, ephemeralOrder]);

  const removedCalculations = useMemo(
    () => calculationsQuery.data?.filter((x) => x.isDeleted).sort(byServerOrder) ?? [],
    [calculationsQuery.data],
  );

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: number[]) =>
      RecipeCalculationQuery.Client.reorder(recipeId, { orderedCalculationIds: orderedIds }),
    onError: () => {
      enqueueSnackbar(t('Page.Recipe.Notification.ReorderFailed'), { variant: 'error' });
      // Roll back to server truth (also recovers the strict-400 stale-list case).
      queryClient.invalidateQueries({
        queryKey: RecipeCalculationQuery.getAllQueryKey(recipeId),
      });
    },
  });

  // Applies a new display order. When `persist` is false (readonly recipes) the order is kept
  // client-side only; otherwise it is optimistically applied and saved on the backend.
  const reorderCalculations = useCallback(
    (orderedIds: number[], persist: boolean) => {
      if (!persist) {
        setEphemeralOrder(orderedIds);
        return;
      }
      reorderRecipeCalculations(queryClient, recipeId, orderedIds);
      reorderMutation.mutate(orderedIds);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, recipeId],
  );

  // Re-sorts the calculations by the given key and applies it as the new order.
  const sortCalculationsBy = useCallback(
    (sortBy: SortRecipeCalculationsBy, persist: boolean) => {
      const orderedIds = [...calculations]
        .sort((a, b) => sortRecipeCalculations(a, b, sortBy))
        .map((x) => x.id);
      reorderCalculations(orderedIds, persist);
    },
    [calculations, reorderCalculations],
  );

  // Drops the transient state a draft card wrote under its negative id once the card goes away.
  const forgetDraft = useCallback(
    (draftId: number) => {
      store.removeStullPoint(draftId);
      store.clearCalculationDirty(draftId);
      if (store.getState().selectedCalculation?.id === draftId) {
        store.selectCalculation(null);
      }
    },
    [store],
  );

  /** Adds a draft card at the end of the board, on its mode-chooser step. */
  const openDraft = useCallback(() => {
    const id = nextDraftIdRef.current--;
    setDrafts((previous) => [...previous, { id, mode: 'chooser', index: calculations.length }]);
    return id;
  }, [calculations.length]);

  /** Switches a draft to one of the three ways of filling it in. */
  const setDraftMode = useCallback(
    (draftId: number, mode: CalculationDraftMode) =>
      setDrafts((previous) =>
        previous.map((draft) => (draft.id === draftId ? { ...draft, mode } : draft)),
      ),
    [],
  );

  /** Hands a picked template to the draft's calculator step, which opens filled in with it. */
  const applyDraftTemplate = useCallback(
    (draftId: number, template: CalculationDraftTemplate) =>
      setDrafts((previous) =>
        previous.map((draft) =>
          draft.id === draftId ? { ...draft, mode: 'calculator', template } : draft,
        ),
      ),
    [],
  );

  const closeDraft = useCallback(
    (draftId: number) => {
      setDrafts((previous) => previous.filter((draft) => draft.id !== draftId));
      forgetDraft(draftId);
    },
    [forgetDraft],
  );

  // Recomputes every draft's position from a board order produced by a drag. Drafts are placed by
  // how many persisted calculations precede them, so dragging a *calculation* past a draft keeps
  // the draft where it looks like it is just as well as dragging the draft itself does.
  const repositionDrafts = useCallback((orderedBoardIds: number[]) => {
    setDrafts((previous) => {
      if (!previous.length) {
        return previous;
      }
      const draftById = new Map(previous.map((draft) => [draft.id, draft]));
      const indexById = new Map<number, number>();
      let placed = 0;
      orderedBoardIds.forEach((id) => {
        if (isCalculationDraftId(id)) {
          indexById.set(id, placed);
        } else {
          placed++;
        }
      });

      // Keep drafts in board order as well as recording how many calculations precede each one.
      // Drafts at the same calculation index rely on this array order when rendered.
      const seen = new Set<number>();
      const reordered = orderedBoardIds.flatMap((id) => {
        const draft = draftById.get(id);
        if (!draft) return [];
        seen.add(id);
        return [draft];
      });
      reordered.push(...previous.filter((draft) => !seen.has(draft.id)));

      return reordered.map((draft) => {
        const index = indexById.get(draft.id);
        return index === undefined || index === draft.index ? draft : { ...draft, index };
      });
    });
  }, []);

  // Adds a calculation created from a draft and persists it at the position the draft occupied, so
  // the new calculator appears where its draft was rather than at the end of the board.
  const commitDraft = useCallback(
    (draftId: number, created: RecipeCalculationDto) => {
      // Clamped: the list can shrink under an open draft (a calculation deleted from another view),
      // which would otherwise strand it past the end of the board.
      const index = Math.min(
        drafts.find((draft) => draft.id === draftId)?.index ?? calculations.length,
        calculations.length,
      );
      setDrafts((previous) => {
        const committedPosition = previous.findIndex((draft) => draft.id === draftId);
        return previous.flatMap((draft, position) => {
          if (draft.id === draftId) return [];
          // Drafts visually after the committed one now have one more persisted calculation before
          // them. For drafts at the same index, their array order is their visual order.
          return draft.index > index ||
            (draft.index === index && committedPosition !== -1 && position > committedPosition)
            ? [{ ...draft, index: draft.index + 1 }]
            : [draft];
        });
      });
      forgetDraft(draftId);

      // The POST is already committed. If navigation or a cache reset removed the list meanwhile,
      // refetch instead of throwing and leaving the successful save looking retryable.
      const queryKey = RecipeCalculationQuery.getAllQueryKey(recipeId);
      if (!queryClient.getQueryData<RecipeCalculationDto[]>(queryKey)) {
        void queryClient.invalidateQueries({ queryKey });
        return;
      }

      addNewRecipeCalculation(queryClient, recipeId, created);
      if (index < calculations.length) {
        const orderedIds = calculations.map((x) => x.id);
        orderedIds.splice(index, 0, created.id);
        reorderCalculations(orderedIds, true);
      }
    },
    [queryClient, recipeId, calculations, drafts, reorderCalculations, forgetDraft],
  );

  // Resets states to default and cleans up Stull chart points.
  useEffect(() => {
    setEphemeralOrder(null);
    setDrafts([]);
    store.resetForRecipe(recipeId);
  }, [recipeId, store]);

  const value = useMemo(
    () => ({
      calculations,
      removedCalculations,
      reorderCalculations,
      sortCalculationsBy,
      areCalculationsLoading: calculationsQuery.isLoading,
      isReorderPending: reorderMutation.isPending,
      drafts,
      openDraft,
      setDraftMode,
      applyDraftTemplate,
      closeDraft,
      repositionDrafts,
      commitDraft,
    }),
    [
      calculations,
      removedCalculations,
      reorderCalculations,
      sortCalculationsBy,
      calculationsQuery.isLoading,
      reorderMutation.isPending,
      drafts,
      openDraft,
      setDraftMode,
      applyDraftTemplate,
      closeDraft,
      repositionDrafts,
      commitDraft,
    ],
  );

  return { value, store };
};

/**
 * Two contexts, on purpose:
 * - `useRecipeCalculations` carries the calculation list and its mutations. It changes rarely
 *   (server data, reorder, pending flags) and is *not* consumed by calculator cards.
 * - `useRecipeCalculationsStore` carries the transient-state store, whose identity never changes,
 *   so subscribing to it never causes a re-render. Read its contents through the selector hooks
 *   below and its writers through `useRecipeCalculationsActions`.
 */
export const [RecipeCalculationsProvider, useRecipeCalculations, useRecipeCalculationsStore] =
  constate(
    useRecipeCalculationsValue,
    (value) => value.value,
    (value) => value.store,
  );

/**
 * Subscribes to one slice of the transient store. The selector must return a primitive or a value
 * whose identity is stable between writes - returning a fresh object/array on every call would
 * re-render on every store notification (and `useSyncExternalStore` would loop).
 */
const useRecipeCalculationsSelector = <T>(selector: (state: RecipeCalculationsState) => T): T => {
  const store = useRecipeCalculationsStore();
  return useSyncExternalStore(store.subscribe, () => selector(store.getState()));
};

/** Writers only. The store's identity is stable, so this never re-renders its caller. */
export const useRecipeCalculationsActions = () => useRecipeCalculationsStore();

export const useStullPoints = () => useRecipeCalculationsSelector((state) => state.stullPoints);

export const useSelectedCalculation = (): SelectedCalculatorState | null =>
  useRecipeCalculationsSelector((state) => state.selectedCalculation);

export const useSelectedCalculationId = () =>
  useRecipeCalculationsSelector((state) => state.selectedCalculation?.id);

/** Boolean-valued, so selecting a card re-renders only the two cards whose flag actually flips. */
export const useIsCalculationSelected = (calculationId: number) =>
  useRecipeCalculationsSelector((state) => state.selectedCalculation?.id === calculationId);

export const useIsCalculationDirty = (calculationId: number | undefined) =>
  useRecipeCalculationsSelector((state) =>
    calculationId === undefined ? false : !!state.dirtyCalculations[calculationId],
  );

export const useAnyDirtyCalculation = () =>
  useRecipeCalculationsSelector((state) =>
    Object.values(state.dirtyCalculations).some((isDirty) => isDirty),
  );
